import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { formatDateWithOptions, formatWeekdayShort, formatWeekdayLong, formatMonthShort, formatDayMonthShort, formatWeekdayMonthDayLong } from '@cf-shared/date-format';
import { CanLeaveResult, CsPage, NavController } from 'providers/nav-controller';
import { IHomePage } from 'pages/home/home-interface';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { FoodDbService } from 'services/food-db.service';
import { Food, FoodHeader, NutrientTargets, ComplexFood, calculateNutrientMatch_pc, INFOODS, isComplexFood, getIngredientText, AnyFood, isLogMealById } from '@cf-shared/data-model/food';
import { mealTargetsAsIfEmpty, tunableTargetsOr } from '@cf-shared/meal-targets-as-if-empty';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { ImageService } from 'services/image-service';
import { MealTuner } from '@cf-shared/meal-tuner';
import { MealTunerMeal, MealTunerOutcome, MealTunerParams } from '@cf-shared/meal-tuner-interface';
import { reportInvalidTunerMeals } from 'util/cannot-tune-reason';
import { SettingsService } from 'services/settings.service';
import { UserDb } from 'services/user-db.service';
import { DatabaseWrapper , DocWatcher, TransactionId } from 'services/database-wrapper';
import { DateTime } from 'luxon';
import { deepClone } from '@cf-shared/deep-helpers';
import { MealSuggestionsPageParams, MealSuggestionsMode, IMealSuggestionsPage } from 'pages/meal-suggestions/meal-suggestions-interface';
import { IEditMealPage } from 'pages/edit-meal/edit-meal-interface';
import { IEditFoodPage } from 'pages/edit-food/edit-food-interface';
import { DebugService } from 'services/debug.service';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { TelemetryService } from 'services/telemetry.service';
import { LoggingService } from 'services/logging.service';
import { FoodReadFailedError, FoodReadFailurePolicy } from 'services/food-read-error';
import { FormatNutrients, formatTime, formatRelativeDay, formatScheduledMealName } from 'util/formatting';
import { LoadingController } from 'providers/loading-controller';
import { ToastController } from 'providers/toast-controller';
import { Log_DayStart } from '@cf-shared/data-model/log-day-start';
import { Log_Meal, markMealPlannedIfItHoldsFood, placeCopiedMealsOnDay } from '@cf-shared/data-model/log-meal';
import { LogRecordType, assureIdForEntry, getEntryLocalTimeInDesiredTimeZone, setEntryLocalTime } from '@cf-shared/data-model/user-log';
import { StepConfig, StepState, StepperStyle } from 'components/cs-stepper/cs-stepper';
import { LikeState } from 'components/cs-food-card/cs-food-card';
import { PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { PopoverController } from 'providers/popover-controller';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { CopyMealsPopover, CopyMealsPopoverInput, CopyMealsPopoverResult } from 'components/copy-meals-popover/copy-meals-popover';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { MealDayCopyRollbackState, captureMealsBeforeDayCopy, restoreMealsAfterFailedDayCopy } from './meal-plan-day-copy-rollback';


// Used internally to display the plan on this page only.
class MealSuggestionEntry {
  time: string;
  text: string;
  // built from food/ingredient names (database content), not translatable catalog text
  shortDescription: TranslatedString;
  image: string;
  match: string;
  food: Food;
  displayNutrients: UiDisplayNutrient[];
  mealLog: Log_Meal;
  isSelected: boolean = false;
  loading: boolean = false;
}

/**
 * Are these the very same meal entries, in the same order?
 *
 * Compared by object identity, not by contents: `loadCurrentDayAsync` builds a brand-new array
 * of brand-new entries every time it loads a day, so a reload underneath a running operation
 * shows up here even when the new day looks exactly like the old one.
 */
function sameEntriesInOrder(current: MealSuggestionEntry[], expected: MealSuggestionEntry[]): boolean {
  return current.length === expected.length && expected.every((entry, i) => current[i] === entry);
}

/**
 * Per-tab day data for A/B recycling (each tab owns its own data).
 *
 * A slot holds ONE day's data, whole. `loadCurrentDayAsync` builds the day it is loading
 * in locals and replaces the slot in a single write, so a slot is never a mixture of the
 * day it used to show and the day it is about to show. Treat the object as immutable once
 * it is in `tabData`: to change what a slot holds, put a new one there.
 *
 * The one exception is `updateMealSuggestionAsync`, which refreshes the totals of the day
 * already on screen and writes them in place. It holds the slot object it started with,
 * and a load of that same slot finishing underneath it swaps in a different object — so
 * those writes can land on the object that was replaced and never be seen. That is the
 * right outcome rather than a gap: the load it lost the race to has already computed the
 * new day's totals from the new day's meals, so the slot on screen is correct either way.
 */
interface TabDayData {
  meals: MealSuggestionEntry[];
  displayNutrientsDay: UiDisplayNutrient[];
  dayName: string;
  dayStart: Log_DayStart;
  match_pc: number | undefined;
  /**
   * The day this slot's data actually belongs to, as an ISO date, or undefined when the
   * slot's contents may not be attributed to any day — it is empty, or a load is running
   * and the slot still shows the day before. Navigation moves `currentDay` and swaps the
   * slot before it awaits the day's data, so the two disagree for as long as the load
   * takes — this is what lets `storeDayInCache` tell the difference.
   */
  loadedDayKey: string | undefined;
}

// What loadCurrentDayAsync managed to do. A day that did not load must not be given
// suggestions: the suggestion code reads the day document the load never found.
enum DayLoadResult {
  Loaded = 'Loaded',
  DayStartMissing = 'DayStartMissing',
}

// Data structure for days pending save (deferred save pattern)
interface PendingDayData {
  dayStart: Log_DayStart;
  meals: Log_Meal[];
  selectedMealIds: Set<string>;  // track which meals are selected for saving
}

/**
 * How close a tuned copy of a recent meal has to come to the meal's targets, as a
 * percentage, before the plan will reuse that meal. A copy scoring below this is not
 * offered at all. Used by every search over recent meals; the whole-day checks
 * elsewhere in this file ask a different question and keep their own number.
 */
const REUSABLE_RECENT_MEAL_MATCH_PC = 95;

@Component({
    selector: 'page-meal-plan',
    templateUrl: 'meal-plan.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MealPlanPage extends CsPage<{}> implements OnDestroy {
  protected LL = LL;

  // A/B recycling slots for cs-tab-content-container animation
  // all per-day data lives in tabData (meals, dayStart, displayNutrientsDay, dayName, match_pc)
  hasClientSmallScreen = hasClientSmallScreen;
  tabSlot: 'a' | 'b' = 'a';
  isNavigatingBack = false;
  tabLoading = { a: false, b: false };
  tabData: Record<'a' | 'b', TabDayData> = {
    a: { meals: [], displayNutrientsDay: [], dayName: '', dayStart: undefined, match_pc: undefined, loadedDayKey: undefined },
    b: { meals: [], displayNutrientsDay: [], dayName: '', dayStart: undefined, match_pc: undefined, loadedDayKey: undefined },
  };

  /**
   * Per day key (an ISO date), the ids of every meal FILED on that day, as
   * `UserDbService.getMealIdsFiledOnDayAsync` reads them: by document id, so it also holds the ids
   * of meals the day does not SHOW. A copy must avoid those too, or it can be saved over one of
   * them (#5466). Filled every time this page reads a day from the database, and read wherever a
   * copy's id is settled.
   */
  private mealIdsFiledOnDay = new Map<string, ReadonlySet<string>>();

  // active tab data shortcut
  get tab(): TabDayData { return this.tabData[this.tabSlot]; }

  loading = false;
  /** Tracks if page has been initialized (to skip re-initialization when returning from child pages like calendar popup) */
  private initialized = false;
  validDateMin: DateTime = undefined;
  validDateMax: DateTime = undefined;
  firstDayString: string = undefined;
  lastDayString: string = undefined;
  dayNames: { num: string; name: string }[] = [];
  stepperLabels: string[] = [];
  datesCorrect = false;

  // expose enums for template
  StepperStyle = StepperStyle;
  LikeState = LikeState;
  protected isLogMealById = isLogMealById;

  get stepperSteps(): StepConfig[] {
    return this.stepperLabels.map((label, i) => {
      // check if this day has selected meals
      const dayForStep = this.firstDayBOD?.plus({ days: i });
      const dayKey = dayForStep?.toISODate();
      const pendingDay = dayKey ? this.pendingDays.get(dayKey) : undefined;
      const selectedCount = pendingDay?.selectedMealIds.size ?? 0;
      const totalMeals = pendingDay?.meals.length ?? 0;
      const hasSelectedMeals = selectedCount > 0;
      const allMealsSelected = totalMeals > 0 && selectedCount === totalMeals;

      // determine state based on day position and meal selection
      let state: StepState;
      if (i < this.currentDayIndex) {
        // past days: complete if all meals selected, partial if some selected
        state = allMealsSelected ? StepState.Complete
          : hasSelectedMeals ? StepState.Partial
            : StepState.Pending;
      } else if (i === this.currentDayIndex) {
        // current day: always show as current
        state = StepState.Current;
      } else {
        // future days: always pending
        state = StepState.Pending;
      }

      return { label, state };
    });
  }

  // Duration options for the dropdown
  selectedDuration: string = '7days';
  durationOptions: { value: string; label: TranslatedString }[] = [];
  firstDayBOD: DateTime = undefined;
  lastDayEOD: DateTime = undefined;
  currentDay: DateTime = undefined;
  /** -1 means open in "preamble" mode, 0 means firstDayBOD */
  currentDayIndex = -1;
  prevDayEnabled = false;
  nextDayEnabled = false;
  /** this is a "hidden" member, don't use directly! use the `recentMeals` through its getter. */
  recentMeals_: Log_Meal[] = [];
  candidates: AnyFood[];
  tunerParams: MealTunerParams;
  foodNameCache: { [id: string]: string } = {};
  // set once isSameMeal has reported a comparison against an uncached name, so a pass that
  // compares many meals files one Sentry problem rather than one per comparison
  private reportedUncachedMealName = false;
  fitnessPlan: FitnessPlan = undefined;
  /** Cache of days modified during this session (not yet saved to DB) */
  private pendingDays: Map<string, PendingDayData> = new Map();
  /**
   * Set when the page is closing itself because planning hit an internal error.
   * The leave guard then lets it go without asking: the user has just been shown
   * an error, and offering "Keep planning" would strand them on a day that
   * failed to load. Every other way out of the page still asks (#5056).
   *
   * The guard clears it as soon as it reads it, so the exemption covers one
   * departure and no more. It has to: the navigation layer ignores a pop while
   * a browser-initiated one is already in flight, so the page can outlive the
   * pop this was set for — and a flag left standing would turn the guard off
   * for the rest of the page's life, silently bringing #5056 back.
   */
  private leavingAfterError = false;

  // reactive sync
  private docWatcher: DocWatcher;

  addRecentMeal(meal: Log_Meal) {
    if (!meal.nutrients[INFOODS.Calories]) {
      // don't store empty meals!
      return;
    }
    // make a clean version of a template that cannot be accidentally saved as actual log entry - to prevent hard to find bugs with duplicate references
    let cleaned = deepClone(meal);
    // if manually composed (no basedOnFoodId), preserve the log meal reference before stripping _id
    if (!cleaned.basedOnFoodId && cleaned._id) {
      cleaned.basedOnFoodId = cleaned._id;
    }
    delete cleaned._id;
    delete cleaned.type;
    // if there is a previous version of same meal stored, remove it
    this.recentMeals_ = this.recentMeals_.filter(m => m.time_utc !== meal.time_utc || m.name !== meal.name);
    this.recentMeals_.push(cleaned);
  }
  get recentMeals(): Log_Meal[] {
    return this.recentMeals_;
  }

  getFoodNameCached(id: string): string {
    return this.foodNameCache[id];
  }

  async cacheFoodNameAsync(id: string): Promise<string> {
    if (!id) {
      return undefined;
    }

    let cached = this.foodNameCache[id];
    if (!cached) {
      let food = await this.foodDb.getFoodOrLogMealByIdAsync(id, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }, this.docWatcher); // (TODO: can we not always cache here?!)
      cached = (food && food.name) || 'unknown';
      this.foodNameCache[id] = cached;
    }
    return cached;
  }

  constructor(
    public navCtrl: NavController,
    public cloudFunctionsService: CloudFunctionsService,
    private foodDb: FoodDbService,
    protected imageService: ImageService,
    protected settings: SettingsService,
    protected userDb: UserDb,
    private debug: DebugService,
    private cdRef: ChangeDetectorRef,
    private telemetry: TelemetryService,
    public logging: LoggingService,
    public loadingCtrl: LoadingController,

    private popupMenu: PopupMenuProvider,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
    private toastCtrl: ToastController
  ) {
    super();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  async ionViewCanEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cdRef.markForCheck();
      });
    }
  }

  async ionViewWillEnter() {
    // skip initialization when returning from child pages (e.g., calendar popup)
    if (this.initialized) {
      return;
    }

    if (this.currentDayIndex < 0) {
      void this.initializeAsync();
    }
  }

  async initializeAsync() {
    this.loading = true;
    this.updateDurationOptions();

    this.fitnessPlan = await this.userDb.loadPlanAsync(this.docWatcher);

    let todayUTC = this.debug.getNowLocal().startOf('day').toUTC();
    let futureDays = (await this.userDb.getLogEntriesInRangeAsync(todayUTC, todayUTC.plus({ days: 14 }).endOf('day'), this.docWatcher)).filter(
      e => e.type === LogRecordType.DayStart,
    ) as Log_DayStart[];
    this.validDateMin = undefined;
    this.validDateMax = undefined;

    if (futureDays && futureDays.length > 0) {
      this.validDateMin = DateTime.fromISO(futureDays[0].time_utc).toLocal();
      this.validDateMax = DateTime.fromISO(futureDays[futureDays.length - 1].time_utc).toLocal();
    }

    let unplannedDays = futureDays.filter(
      day =>
        /* beware that `! (a >= b)` is not the same thing as `a < b` if `a === undefined`! */
        !(calculateNutrientMatch_pc(day.nutrients, day.targets) >= 95),
    );
    if (unplannedDays.length > 0) {
      this.firstDayString = DateTime.fromISO(unplannedDays[0].time_utc).toLocal().startOf('day').toISODate();
    } else if (futureDays && futureDays.length > 0) {
      this.firstDayString = this.validDateMin.toISODate();
    }

    this.currentDayIndex = -1;
    this.currentDay = undefined;

    // set default duration and calculate end date
    this.selectedDuration = '7days';
    this.updateLastDayFromDuration();

    this.loading = false;
    this.initialized = true;
  }

  handleDatesChanged() {
    this.datesCorrect = DateTime.fromISO(this.firstDayString) <= DateTime.fromISO(this.lastDayString);
  }

  handleStartDateChanged() {
    this.updateLastDayFromDuration();
    this.cdRef.markForCheck();
  }

  handleDurationChanged() {
    this.updateLastDayFromDuration();
    this.cdRef.markForCheck();
  }

  private updateLastDayFromDuration() {
    if (!this.firstDayString) {
      return;
    }

    const startDate = DateTime.fromISO(this.firstDayString);
    let endDate: DateTime;

    switch (this.selectedDuration) {
      case '7days':
        endDate = startDate.plus({ days: 6 });
        break;
      case '14days':
        endDate = startDate.plus({ days: 13 });
        break;
      case 'untilSunday':
        // find next Sunday (weekday 7 in Luxon)
        endDate = startDate.plus({ days: (7 - startDate.weekday) % 7 || 7 });
        break;
      case 'untilSaturday':
        // find next Saturday (weekday 6 in Luxon)
        endDate = startDate.plus({ days: (6 - startDate.weekday + 7) % 7 || 7 });
        break;
      default:
        endDate = startDate.plus({ days: 6 });
    }

    // clamp to validDateMax if needed
    if (this.validDateMax && endDate > this.validDateMax) {
      endDate = this.validDateMax;
    }

    this.lastDayString = endDate.toISODate();
    this.handleDatesChanged();
  }

  private updateDurationOptions() {
    this.durationOptions = [
      { value: '7days', label: LL.mealPlan.duration.days7() },
      { value: '14days', label: LL.mealPlan.duration.days14() },
      { value: 'untilSunday', label: LL.mealPlan.duration.untilSunday() },
      { value: 'untilSaturday', label: LL.mealPlan.duration.untilSaturday() }
    ];
  }

  handleStartMealPlanning() {
    void this.startMealPlanningAsync();
  }

  handleAbortInitial() {
    this.telemetry.trackEvent('action_mealplan_nothanks', {});
    void this.exitAsync({ completed: false });
  }

  handleAbort() {
    this.telemetry.trackEvent('action_mealplan_skiprest', {});
    void this.skipRestAndGoHomeAsync();
  }

  /**
   * "Skip Rest" leaves for the home page rather than going back, and setting a
   * new root page does NOT run the leave guard — so this path has to ask for
   * itself. Every other way out goes through ionViewCanLeave below.
   */
  private async skipRestAndGoHomeAsync() {
    if (await this.confirmLeaveAsync()) {
      await this.navCtrl.setRootAsync(IHomePage, {});
    }
  }

  /**
   * Close the page because planning failed, without asking about unsaved days.
   * The user has just been shown an error toast; raising the exit dialog on top
   * of it would only offer them "Keep planning" on a day that cannot load.
   */
  private async exitAfterErrorAsync() {
    this.leavingAfterError = true;
    await this.exitAsync({ completed: false });
  }

  async exitAsync(options?: { completed?: boolean }) {
    const completed = options?.completed ?? false;
    if (completed) {
      // user finished via action button - go to home
      return this.navCtrl.setRootAsync(IHomePage, {});
    } else {
      // user closed without finishing - go back in history
      return this.navCtrl.popAsync();
    }
  }

  /**
   * Ask whether the page may be left, and answer true when it may.
   *
   * This is the ONE place that decides it. The navigation layer calls it
   * through ionViewCanLeave below for every way of leaving that closes the page
   * — the header close button, the Android hardware back button, the browser
   * back button and a swipe back — and "Skip Rest" calls it directly because
   * setting a new root page bypasses the guard. It never navigates itself;
   * whoever asked does the leaving.
   */
  private async confirmLeaveAsync(): Promise<boolean> {
    this.storeDayInCache();

    // if nothing modified, leaving is free
    if (this.pendingDays.size === 0) {
      return true;
    }

    const result = await this.alertCtrl.showAlertAsync<'save' | 'continue' | 'discard'>({
      title: LL.mealPlan.exitDialog.title(),
      message: LL.mealPlan.exitDialog.message(),
      buttons: [
        {
          text: LL.mealPlan.exitDialog.saveAndExit(),
          role: AlertButtonRole.Primary,
          result: 'save'
        },
        {
          text: LL.mealPlan.exitDialog.keepPlanning(),
          // no role - stays in middle after sorting
          result: 'continue'
        },
        {
          text: LL.mealPlan.exitDialog.discardAndExit(),
          role: AlertButtonRole.Cancel,
          result: 'discard'
        }
      ]
    });

    if (result === 'save') {
      try {
        await this.commitAllPendingChangesAsync();
      } catch (err) {
        // saving IS this answer, so a failed save must not read as "yes, you may
        // leave" — that would close the page and lose the days the user just
        // asked to keep. Report it, tell the user, and keep them here with the
        // days still pending so they can try again. Answering false rather than
        // rethrowing matters: the navigation layer turns a thrown error into a
        // bare console.log, which nobody sees in production.
        this.logging.trackAbnormalException(err, LL.mealPlan.errors.saveFailed());
        return false;
      }
      return true;
    }
    if (result === 'discard') {
      this.pendingDays.clear();
      return true;
    }
    // 'continue' - stay on the page, cancelling whatever navigation asked
    return false;
  }

  /**
   * The app's general "may I leave this page?" hook. cs-nav runs it before it
   * closes a page, so this is what makes the Android hardware back button, the
   * browser back button and a swipe back ask about unsaved days instead of
   * throwing them away (#5056).
   */
  async ionViewCanLeave(): Promise<CanLeaveResult<void>> {
    // if the page is closing itself after a planning error, let it go once and
    // put the guard straight back on - see the field's own comment
    if (this.leavingAfterError) {
      this.leavingAfterError = false;
      return true;
    }
    return this.confirmLeaveAsync();
  }

  /** Set vote up/down */
  async voteAsync(food: Food, direction: -1 | 1) {
    if (food) {
      food.local = food.local || {};
      this.foodDb.voteFood(food, direction);
      let saving = this.foodDb.saveUserFoodAsync(food, direction === -1 ? 'cached' : 'used');

      // if downvoted -  remove its suggestion from the current list
      if (food.local && food.local.liked < 0) {
        for (let meal of this.tab.meals) {
          if (meal.food._id !== food._id) {
            continue;
          }
          meal.food = undefined;
          meal.mealLog.ingredients = [];
        }
        // ... and refill the list
        await this.fillCurrentDaySuggestionsAsync();
      }

      this.telemetry.trackEvent('action_mealplan_vote', { direction });

      return saving; // so someone can wait if needed somewhere
    }
    return null;
  }

  handleNextAll() {
    void this.nextAllAsync();
  }

  handleNextDay() {
    void this.nextDayAsync();
  }

  handlePrevDay() {
    void this.prevDayAsync();
  }

  handleStepperClick(dayIndex: number) {
    void this.goToDayAsync(dayIndex);
  }

  handleDone() {
    void this.doneAsync();
  }

  handleEditMeal(meal: MealSuggestionEntry) {
    void this.editMealAsync(meal);
    this.telemetry.trackEvent('action_mealplan_edit', {});
  }

  handlePickMeal(meal: MealSuggestionEntry, event: Event) {
    event.stopPropagation();
    void this.pickMealAsync(meal);
    this.telemetry.trackEvent('action_mealplan_pick', {});
  }

  /**
   * Display name for a meal row (#4317). The stored name is canonical English
   * ("Meal 3"), so translate it wherever it reaches the screen — the row header,
   * and the copy dialog, whose surrounding sentence is already translated.
   */
  protected mealDisplayName(meal: MealSuggestionEntry): string {
    return formatScheduledMealName(meal.mealLog.name);
  }

  handleCopyMeal(meal: MealSuggestionEntry, event: Event) {
    event.stopPropagation();
    void this.showCopyPopoverAsync(this.mealDisplayName(meal), [meal], event.currentTarget as HTMLElement);
  }

  handleToggleMealSelection(meal: MealSuggestionEntry, event: Event) {
    event.stopPropagation();
    meal.isSelected = !meal.isSelected;
    this.cdRef.markForCheck();
  }

  handleCopyMeals(event: Event) {
    const selectedMeals = this.tab.meals.filter(m => m.isSelected);
    if (selectedMeals.length === 0) {
      return;
    }
    const mealDescription = selectedMeals.length === 1
      ? this.mealDisplayName(selectedMeals[0])
      : LL.mealPlan.copyDialog.multipleMeals({ count: selectedMeals.length });
    void this.showCopyPopoverAsync(mealDescription, selectedMeals, event.currentTarget as HTMLElement);
  }

  private async showCopyPopoverAsync(mealDescription: string, mealsToCopy: MealSuggestionEntry[], targetElement: HTMLElement) {
    // Build list of available days (excluding current day)
    const totalDays = this.stepperLabels.length;
    const availableDays: DateTime[] = [];
    for (let i = 0; i < totalDays; i++) {
      if (i !== this.currentDayIndex) {
        availableDays.push(this.firstDayBOD.plus({ days: i }));
      }
    }

    if (availableDays.length === 0) {
      return; // no other days to copy to
    }

    const input: CopyMealsPopoverInput = {
      mealDescription,
      availableDays,
      currentDayIndex: this.currentDayIndex
    };

    const result = await this.popoverCtrl.present(CopyMealsPopover,
      { input },
      {
        targetFromElement: targetElement,
        targetToElement: document.body,
        animate: true,
        showBackdrop: true
      }
    ) as { resultData: CopyMealsPopoverResult | null };

    if (result?.resultData?.selectedDayIndices?.length > 0) {
      this.copyMealsToDays(mealsToCopy, result.resultData.selectedDayIndices);
    }
  }

  private copyMealsToDays(mealsToCopy: MealSuggestionEntry[], targetDayIndices: number[]) {
    // Adjust indices since availableDays excludes current day
    const actualDayIndices = targetDayIndices.map(idx => {
      // availableDays is built excluding currentDayIndex
      // so we need to map back to actual day indices
      let actualIdx = idx;
      if (idx >= this.currentDayIndex) {
        actualIdx = idx + 1;
      }
      return actualIdx;
    });

    // Copy meals to each target day's pending cache
    for (const dayIdx of actualDayIndices) {
      const targetDay = this.firstDayBOD.plus({ days: dayIdx });
      const dayKey = targetDay.toISODate();

      // Get or create pending data for this day
      let pendingDay = this.pendingDays.get(dayKey);
      if (!pendingDay) {
        // Create empty pending day - dayStart will be loaded when navigating to this day
        pendingDay = {
          dayStart: null,
          meals: [],
          selectedMealIds: new Set()
        };
      }

      // Copy each meal - replace existing meal with same name or add if not found
      for (const meal of mealsToCopy) {
        const copiedMeal = deepClone(meal.mealLog);
        // Move the copy to the target day, keeping the time of day the user sees on the source
        // meal. Both calls are needed because the source may have been written on a device in
        // another time zone: reading time_utc raw would copy the wrong hour, and assigning it
        // raw would leave the source's offset behind. See design-docs/sync-integrity.md, "A Log
        // Entry's Two Time Fields Must Always Move Together" (#4728).
        const originalTime = getEntryLocalTimeInDesiredTimeZone(copiedMeal, targetDay);
        const newTime = targetDay.set({
          hour: originalTime.hour,
          minute: originalTime.minute
        });
        setEntryLocalTime(copiedMeal, newTime);
        // start from the natural id for the new time; placeCopiedMealsOnDay settles the real one
        copiedMeal._id = undefined;
        assureIdForEntry(copiedMeal);

        // replace the meal with the same name, or add it. If this day was never opened, its
        // pending list holds only copies, and the copies meet the day's stored meals later — when
        // the day is opened (loadCurrentDayAsync) or when everything is saved
        // (commitAllPendingChangesAsync).
        pendingDay.meals = placeCopiedMealsOnDay(pendingDay.meals, [copiedMeal], this.mealIdsFiledOnDay.get(dayKey));
        pendingDay.selectedMealIds.add(copiedMeal._id);
      }

      this.pendingDays.set(dayKey, pendingDay);
    }

    this.telemetry.trackEvent('action_mealplan_copy', {
      mealCount: mealsToCopy.length,
      targetDays: actualDayIndices.length
    });
  }

  get hasSelectedMeals(): boolean {
    return this.tab.meals.some(m => m.isSelected);
  }

  handleAcceptAllMeals() {
    // mark all meals in current day as selected
    for (let meal of this.tab.meals) {
      meal.isSelected = true;
    }
    this.cdRef.markForCheck();
    // move to next day, or finish if on last day
    if (this.nextDayEnabled) {
      void this.nextDayAsync();
    } else {
      void this.doneAsync();
    }
  }

  private isSameMeal(basedIdA: string, basedIdB: string): boolean {
    // if either meal is not based on something, it is not supported for automatic matching
    if (basedIdA === undefined || basedIdB === undefined) {
      return false;
    }
    if (basedIdA === basedIdB) {
      return true;
    }
    let nameA = this.getFoodNameCached(basedIdA);
    let nameB = this.getFoodNameCached(basedIdB);

    // if a name was never cached, the caller skipped cacheFoodNameAsync -- a wiring mistake,
    // not a fact about the food. this method is synchronous and cannot fetch, so it answers
    // "not the same meal", like any other meal it cannot identify, and reports it once per page
    // so the crash this used to be is still visible (df-4a6c85ce5620)
    if (nameA === undefined || nameB === undefined) {
      if (!this.reportedUncachedMealName) {
        this.reportedUncachedMealName = true;
        this.logging.trackSilentProblem('meal_plan_same_meal_name_not_cached', {
          basedIdA,
          basedIdB,
          missingA: nameA === undefined,
          missingB: nameB === undefined,
        });
      }
      return false;
    }

    let [, untilBracketA] = nameA.match(/(.*)\(/) || [, nameA];
    let [, untilBracketB] = nameB.match(/(.*)\(/) || [, nameB];
    untilBracketA = untilBracketA.trim();
    untilBracketB = untilBracketB.trim();
    return untilBracketA === untilBracketB;
  }

  /**
   * The foods the day currently on screen already has planned, read once.
   *
   * The page keeps two tab slots and swaps between them for the day-sliding animation, so
   * `this.tab` is whichever slot is showing AT THIS MOMENT. A search over recent meals awaits
   * per candidate - it caches a food name, then runs the meal tuner - so reading `this.tab`
   * from inside such a loop lets the day being compared against change halfway through one
   * search. Each search calls this on its first line instead, and compares every candidate
   * against the same day (df-d338666b50cd).
   */
  private plannedFoodIdsOnScreenNow(): string[] {
    return this.tab.meals.map(m => m.mealLog.basedOnFoodId);
  }

  // true if this recent meal is already planned among the given foods, so reusing it would plan it twice
  private isAlreadyPlanned(recentMeal: Log_Meal, plannedFoodIds: string[]): boolean {
    return plannedFoodIds.some(plannedFoodId => this.isSameMeal(recentMeal.basedOnFoodId, plannedFoodId));
  }

  /**
   * Copies one recent meal and tunes the copy towards the targets, leaving the entry in
   * `recentMeals` untouched. Answers undefined when the tuned copy does not come close
   * enough to the targets to be worth reusing.
   *
   * This is the part every search over recent meals does the same way. What each search
   * decides for itself is which meals it offers here, what it does with a meal this
   * rejects, and which of the accepted ones it keeps.
   */
  private async tuneRecentMealCopyIfCloseEnoughAsync(recentMeal: Log_Meal, targets: NutrientTargets): Promise<{ meal: Log_Meal; match_pc: number } | undefined> {
    let potentialMatch = deepClone(recentMeal);
    let match_pc = await this.tuneOrAssessMealAsync(potentialMatch, targets);
    if (match_pc < REUSABLE_RECENT_MEAL_MATCH_PC) {
      return undefined;
    }
    return { meal: potentialMatch, match_pc };
  }

  /**
   * This is used to copy one day exactly to today - with eventual tuning.
   *
   * Count the ways this differs from the three searches below before collapsing any of them
   * together, because they are easy to miss:
   *
   * - It does NOT skip a meal already planned in the day on screen. That reads as intended:
   *   copying one day exactly is a direct reason to keep such a meal.
   * - It keeps the FIRST acceptable candidate. The others pick a winner instead - two by the
   *   latest `time_utc`, one by the highest score.
   *
   * A meal of this name that does not fit is skipped, like in the other three searches. A day
   * can log two meals under one name, and the second may fit when the first does not.
   */
  private async findMatchByExactDayAndNameAsync(day: DateTime, mealName: string, targets: NutrientTargets): Promise<Log_Meal> {
    for (let recentMeal of this.recentMeals) {
      await this.cacheFoodNameAsync(recentMeal.basedOnFoodId);

      if (!DateTime.fromISO(recentMeal.time_utc).toLocal().hasSame(day, 'day')) {
        continue;
      }

      if (recentMeal.name !== mealName) {
        continue;
      }

      let candidate = await this.tuneRecentMealCopyIfCloseEnoughAsync(recentMeal, targets);
      // if this meal does not fit, try the next meal of the same day and name
      if (candidate === undefined) {
        continue;
      }
      // take the first acceptable candidate, whether or not a later one is more recent
      return candidate.meal;
    }
    return undefined;
  }

  private async findMatchByDayAndNameAsync(weekDay: number, mealName: string, targets: NutrientTargets): Promise<Log_Meal> {
    // read the day on screen once, before the loop - see plannedFoodIdsOnScreenNow
    const plannedFoodIds = this.plannedFoodIdsOnScreenNow();
    let best: Log_Meal = undefined;
    for (let recentMeal of this.recentMeals) {
      await this.cacheFoodNameAsync(recentMeal.basedOnFoodId);
      if (this.isAlreadyPlanned(recentMeal, plannedFoodIds)) {
        continue;
      }
      let mealWeekDay = DateTime.fromISO(recentMeal.time_utc).toLocal().weekday;
      if (mealWeekDay !== weekDay || recentMeal.name !== mealName) {
        continue;
      }
      let candidate = await this.tuneRecentMealCopyIfCloseEnoughAsync(recentMeal, targets);
      if (candidate === undefined) {
        continue;
      }

      // match the one that is good enough, but was used latest (ISO times can be compared as strings)
      if (best === undefined || candidate.meal.time_utc > best.time_utc) {
        best = candidate.meal;
      }
    }
    return best;
  }

  private async findBestMatchByNameAsync(mealName: string, targets: NutrientTargets): Promise<Log_Meal> {
    // read the day on screen once, before the loop - see plannedFoodIdsOnScreenNow
    const plannedFoodIds = this.plannedFoodIdsOnScreenNow();
    let best: Log_Meal = undefined;
    for (let recentMeal of this.recentMeals) {
      await this.cacheFoodNameAsync(recentMeal.basedOnFoodId);
      if (this.isAlreadyPlanned(recentMeal, plannedFoodIds)) {
        continue;
      }
      if (recentMeal.name !== mealName) {
        continue;
      }
      let candidate = await this.tuneRecentMealCopyIfCloseEnoughAsync(recentMeal, targets);
      if (candidate === undefined) {
        continue;
      }

      // match the one that is good enough, but was used latest (ISO times can be compared as strings)
      if (best === undefined || candidate.meal.time_utc > best.time_utc) {
        best = candidate.meal;
      }
    }
    return best;
  }

  private async findMatchByTargetsAsync(targets: NutrientTargets): Promise<Log_Meal> {
    // read the day on screen once, before the loop - see plannedFoodIdsOnScreenNow
    const plannedFoodIds = this.plannedFoodIdsOnScreenNow();
    let best_pc = 0;
    let best: Log_Meal = undefined;
    for (let recentMeal of this.recentMeals) {
      await this.cacheFoodNameAsync(recentMeal.basedOnFoodId);
      if (this.isAlreadyPlanned(recentMeal, plannedFoodIds)) {
        continue;
      }
      let candidate = await this.tuneRecentMealCopyIfCloseEnoughAsync(recentMeal, targets);
      if (candidate === undefined) {
        continue;
      }
      // this search keeps the closest match to the targets, not the most recent one
      if (candidate.match_pc > best_pc) {
        best_pc = candidate.match_pc;
        best = candidate.meal;
      }
    }
    return best;
  }

  private async tuneMealAsync(meal: Log_Meal, targets: NutrientTargets): Promise<number> {
    let tuningSource = await this.foodDb.getFoodOrLogMealByIdAsync(meal.basedOnFoodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }, this.docWatcher); // (TODO: can we not always cache here?!)
    if (!tuningSource) {
      return 0;
    }
    let tuningParams = await this.foodDb.prepareTuningParamsFoodsAsync([tuningSource]);
    tuningParams.targets = targets;

    let tuner = new MealTuner();
    let results = tuner.solve(tuningParams);

    // the tuner crashed. it catches everything it throws and answers with an empty meal list plus
    // a message, so the empty list alone cannot tell that apart from the honest "no combination of
    // these foods fits" below -- and scoring a crash as a candidate worth zero is the only trace
    // it left anywhere.
    //
    // asked through `outcome`: a failed solve carries no meal list at all, so reading the answer below
    // without this check does not compile.
    //
    // this still returns 0, so the planner goes on skipping this candidate exactly as before --
    // deliberately: the tuner catching its own crash is decided (#5776), and the reason is on
    // `MealTunerResults` in `meal-tuner-interface.ts`.
    if (results.outcome === MealTunerOutcome.Failed) {
      this.logging.trackSilentProblem('meal_tuner_crashed', {
        error: results.error,
        where: 'tuneMealAsync',
        basedOnFoodId: meal.basedOnFoodId,
        targets,
      });
      return 0;
    }

    // guard against meal tuner returning no results
    if (!results.meals || results.meals.length === 0) {
      return 0;
    }

    await this.foodDb.fillMealFromTunerAsync(meal, results.meals[0]);

    if (isComplexFood(tuningSource)) {
      // if the source food is disliked, we don't want to use it
      // this happens when the user disliked the food, but it was still used in a meal from before we want to fall back to recommending a different one
      const local = (tuningSource as Food).local;
      if (local && local.liked < 0) {
        return 0;
      }
    }

    return results.meals[0].precision_pc;
  }

  /** Tune a given meal to particular targets (if it is tunable) - or just assess it's match if it can't be tuned.
   * Note that while calculateNutrientMatch_pc() returns `undefined` for empty meals, this one returns 0!
   */
  private async tuneOrAssessMealAsync(meal: Log_Meal, targets: NutrientTargets): Promise<number> {
    if (meal.basedOnFoodId) {
      return this.tuneMealAsync(meal, targets);
    } else {
      return calculateNutrientMatch_pc(meal.nutrients, targets) || 0;
    }
  }

  private findMatchFromSuggestions(suggestions: MealTunerMeal[]): MealTunerMeal {
    let best: MealTunerMeal = undefined;
    for (let meal of suggestions) {
      if (this.tab.meals.some(m => this.isSameMeal(meal.id, m.mealLog.basedOnFoodId))) {
        continue;
      }
      best = meal;
      break;
    }
    return best;
  }

  async fillEntryFromMealLogAsync(meal: MealSuggestionEntry, log: Log_Meal) {
    meal.mealLog = log;

    // every other display field below is rebuilt from the log, and this one must be too: the
    // entry objects are reused, and neither branch below clears a food reference the entry is
    // already holding. So a meal that LOST its basedOnFoodId -- which is what putting back an
    // abandoned day copy does -- would go on showing the rejected day's title on a meal that is
    // now empty (df-9f2f7eb82990).
    meal.food = undefined;

    // resolve food reference for display
    if (log.basedOnFoodId && !isLogMealById(log.basedOnFoodId)) {
      // recipe or food ID — fetch the actual food
      meal.food = await this.foodDb.getFoodOrLogMealByIdAsync(log.basedOnFoodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }, this.docWatcher) as Food;
    } else if (log.basedOnFoodId && isLogMealById(log.basedOnFoodId)) {
      // log meal ID — create a synthetic FoodHeader with a descriptive name
      const originalMeal = await this.foodDb.getFoodOrLogMealByIdAsync(log.basedOnFoodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }, this.docWatcher) as Log_Meal;
      if (originalMeal) {
        const mealTime = DateTime.fromISO(originalMeal.time_utc).toLocal();
        const dateLabel = formatRelativeDay(this.debug.getNowLocal(), mealTime);
        meal.food = { _id: log.basedOnFoodId, name: `${formatScheduledMealName(originalMeal.name)}, ${dateLabel}` } as Food;
      }
    }

    let match = log.nutrients ? calculateNutrientMatch_pc(log.nutrients, log.targets) : undefined;
    meal.match = match ? match.toFixed(0) : '0';
    meal.displayNutrients = FormatNutrients(log.nutrients, this.userDb, { targets: log.targets });
    meal.image = undefined;

    if (meal.food) {
      meal.text = meal.food.name;
      meal.shortDescription = untranslatedString(getIngredientText(meal.mealLog.ingredients));
    } else if (log.ingredients?.length) {
      // no food reference but has ingredients (e.g. manually composed meal) — use meal name + date as title
      const mealTime = DateTime.fromISO(log.time_utc).toLocal();
      const dateLabel = formatRelativeDay(this.debug.getNowLocal(), mealTime);
      meal.food = { _id: log._id, name: `${formatScheduledMealName(log.name)}, ${dateLabel}` } as Food;
      meal.text = meal.food.name;
      meal.shortDescription = untranslatedString(getIngredientText(log.ingredients));
    } else {
      // nothing to show for this meal. The card's description is bound straight to this field
      // and does not depend on the title, so it has to be emptied here too -- an entry object
      // is reused, and otherwise an emptied meal keeps the ingredient list of whatever was on
      // it before (df-9f2f7eb82990).
      meal.text = '';
      meal.shortDescription = untranslatedString('');
    }

    meal.time = formatTime(DateTime.fromISO(log.time_utc).toLocal(), this.settings);
  }

  private async updateMealSuggestionAsync(meal: MealSuggestionEntry) {
    const tab = this.tab;

    // show meal card update first for responsiveness
    await this.cacheFoodNameAsync(meal.mealLog.basedOnFoodId);
    await this.fillEntryFromMealLogAsync(meal, meal.mealLog);
    this.cdRef.markForCheck();

    // then recalculate nutrients and refresh display with final values.
    //
    // A food that could not be READ makes this throw rather than quietly producing a total with
    // that ingredient missing from it. This is a background refresh of a suggestion card and it
    // saves nothing, so keep whatever numbers the meal already carries and carry on to the
    // display work below — the card must not be left spinning, and a later refresh picks the
    // real values up once the read works. It is deliberately silent: a connectivity blip is not
    // worth a Sentry issue (the same call this makes reports the genuinely unexpected read
    // errors itself, and queryElasticAsync in that service makes the same split), and there is
    // nothing here for the user to act on because nothing of theirs was dropped.
    try {
      await this.foodDb.recalculateMealNutrientsAsync(meal.mealLog);
    } catch (e) {
      if (!(e instanceof FoodReadFailedError)) {
        throw e;
      }
    }
    this.userDb.updateDayValuesInMemory(
      tab.dayStart,
      tab.meals.map(m => m.mealLog),
      [],
    );
    tab.displayNutrientsDay = FormatNutrients(tab.dayStart.nutrients, this.userDb, { targets: tab.dayStart.targets });
    tab.match_pc = calculateNutrientMatch_pc(tab.dayStart.nutrients, tab.dayStart.targets);
    await this.fillEntryFromMealLogAsync(meal, meal.mealLog);
    meal.loading = false;
    this.cdRef.markForCheck();
  }

  public async startMealPlanningAsync() {
    this.loading = true;
    try {
      // gate on auto-plan recipe cache (see awaitAutoPlanFoodsCachedWithTimeoutAsync
      // for rationale — solver throws on missing ingredient food, #3245).
      const cacheReady = await this.foodDb.awaitAutoPlanFoodsCachedWithTimeoutAsync(60_000);
      if (!cacheReady) {
        this.toastCtrl.showToast({
          message: LL.mealSuggestions.errors.recipeLibraryNotReady(),
          showCloseButton: true,
          duration: 10_000,
        });
        void this.exitAfterErrorAsync();
        return;
      }

      this.firstDayBOD = DateTime.fromISO(this.firstDayString).startOf('day');
      this.lastDayEOD = DateTime.fromISO(this.lastDayString).endOf('day');

      this.dayNames = [];
      this.stepperLabels = [];
      this.currentDay = this.firstDayBOD.plus({ days: this.currentDayIndex });
      for (let day = this.firstDayBOD; day < this.lastDayEOD; day = day.plus({ days: 1 })) {
        this.dayNames.push({ num: formatDateWithOptions(day, BOOT_LOCALE, { day: '2-digit' }), name: formatWeekdayShort(day, BOOT_LOCALE) });
        // stepper label format: "Oct 7"
        this.stepperLabels.push(formatDayMonthShort(day, BOOT_LOCALE));
      }

      // load all meals from previous weeks using shared method
      this.recentMeals_ = await this.foodDb.getRecentMealsAsync(14, this.docWatcher);

      this.currentDayIndex = 0;
      this.currentDay = this.firstDayBOD;

      this.candidates = await this.foodDb.getMealFoodCandidatesAsync(this.docWatcher, { searchOnlyLocal: true });
      this.tunerParams = await this.foodDb.prepareTuningParamsFoodsAsync(this.candidates as ComplexFood[]);
      let toCache = [];
      for (let m of this.candidates) {
        if (m._id) {
          toCache.push(this.cacheFoodNameAsync(m._id));
        }
      }
      await Promise.all(toCache);

      if ((await this.loadCurrentDayAsync()) === DayLoadResult.Loaded) {
        await this.fillCurrentDaySuggestionsAsync();
      }
    } catch (err) {
      // symmetric to meal-suggestions.ts:221 — surface the failure as a toast
      // and exit rather than leaking an unhandled rejection. matters now that
      // awaitAutoPlanFoodsCachedWithTimeoutAsync re-throws non-TimeoutError
      // rejections (food-db.service.ts:117-127); without this catch a future
      // rejection of whenAutoPlanFoodsCached would silently strand the page.
      console.error(err);
      this.toastCtrl.showToast({
        message: LL.mealSuggestions.errors.serviceUnavailable(),
        showCloseButton: true,
        duration: 10000,
      });
      void this.exitAfterErrorAsync();
    } finally {
      // ensure loading clears even if any step above throws — pre-existing gap,
      // worth fixing while we're here so the page doesn't get stuck on transient errors.
      this.loading = false;
    }
  }

  /**
   * a meal that the user has put food into is no longer merely scheduled.
   * Two callers, and between them they cover both save paths: storeDayAsync
   * promotes as it saves the current day, and storeDayInCache promotes as it
   * caches a day. commitAllPendingChangesAsync does NOT promote; it writes
   * whatever state the meals already carry. So do not read the storeDayInCache
   * call as redundant — dropping it leaves meals saved on the way out still
   * Scheduled.
   */
  private promoteMealIfPlanned(mealLog: Log_Meal) {
    markMealPlannedIfItHoldsFood(mealLog);
  }

  /**
   * Write one meal out. Both save paths go through here so the re-canonicalize
   * step below cannot be forgotten by one of them.
   *
   * re-canonicalize: entries were deepCloned during load to isolate edits,
   * so merge data back into the identity map's canonical reference before saving.
   * See design-docs/sync-integrity.md.
   */
  private async persistMealAsync(mealLog: Log_Meal, transaction: TransactionId) {
    const canonical = this.userDb.database.ensureCachedReference(mealLog);
    await this.userDb.saveEntryAsync(transaction, canonical);
    await this.foodDb.markMealAsUsedAsync(canonical);
  }

  /**
   * Put the meals copied onto a never-opened day onto that day's stored meals, and return the
   * merged list. Each copy may be given a different id (see placeCopiedMealsOnDay), so the day's
   * selection is rebuilt from the copies themselves rather than re-keyed: two copies can arrive
   * with one id, and an old id would not say which copy it was. On such a day only copies are
   * ever selected, so the rebuilt set loses nothing.
   */
  private placePendingCopiesOnDay(pendingDay: PendingDayData, storedMeals: Log_Meal[], dayKey: string): Log_Meal[] {
    const selectedCopies = pendingDay.meals.filter(m => pendingDay.selectedMealIds.has(m._id));
    const merged = placeCopiedMealsOnDay(storedMeals, pendingDay.meals, this.mealIdsFiledOnDay.get(dayKey));
    pendingDay.selectedMealIds = new Set(selectedCopies.map(m => m._id));
    return merged;
  }

  /** Write one day document out, re-canonicalizing it first for the same reason. */
  private async persistDayAsync(dayStart: Log_DayStart, transaction: TransactionId) {
    const canonicalDay = this.userDb.database.ensureCachedReference(dayStart);
    await this.userDb.saveEntryAsync(transaction, canonicalDay);
  }

  private async storeDayAsync() {
    const tab = this.tab;
    this.loading = true;
    try {
      // at the end of the day store updated day values...
      for (let meal of tab.meals) {
        this.promoteMealIfPlanned(meal.mealLog);
        await this.persistMealAsync(meal.mealLog, DatabaseWrapper.getTransactionId());

        this.addRecentMeal(meal.mealLog);
      }
      await this.persistDayAsync(tab.dayStart, DatabaseWrapper.getTransactionId());
      // We need to clear the backlog because we deepCloned the saved logs and cached backlog is not updated with this save.
      await this.userDb.clearBacklogCacheAsync();
    } finally {
      this.loading = false;
    }
  }

  /** Store current day changes in the pending cache (does NOT save to DB) */
  private storeDayInCache() {
    // if in preamble state (before Generate Meals clicked), nothing to cache
    if (this.currentDayIndex < 0) {
      return;
    }

    // the active tab may not hold this day yet. Starting up and every day
    // navigation set currentDay first and fill the tab in several awaits later,
    // and anything that leaves the page in that window lands here — the header
    // close icon, browser or hardware Back, a swipe back, "Skip Rest". Caching
    // then would record a day the user has never seen: an empty one at start-up,
    // or the previous-but-one day's data once the A/B slots have swapped. Both
    // make the exit dialog claim there are unsaved days, and the empty one makes
    // commitAllPendingChangesAsync throw on a day document that does not exist.
    const dayKey = this.currentDay.toISODate();
    if (this.tab.loadedDayKey !== dayKey) {
      return;
    }

    const tab = this.tab;

    // update meal states (Scheduled → Planned if has ingredients)
    for (let meal of tab.meals) {
      this.promoteMealIfPlanned(meal.mealLog);
      // add to template meals for matching on subsequent days
      this.addRecentMeal(meal.mealLog);
    }

    // cache this day's data including which meals are selected
    const selectedMealIds = new Set<string>();
    for (let meal of tab.meals) {
      if (meal.isSelected && meal.mealLog._id) {
        selectedMealIds.add(meal.mealLog._id);
      }
    }

    this.pendingDays.set(dayKey, {
      dayStart: tab.dayStart,
      meals: tab.meals.map(m => m.mealLog),
      selectedMealIds
    });
  }

  /** Commit all pending day changes to the database */
  private async commitAllPendingChangesAsync() {
    this.loading = true;
    try {
      const transaction = DatabaseWrapper.getTransactionId();

      for (const [dayKey, pendingDay] of this.pendingDays) {
        // a day the user copied meals onto but never opened holds only the copies, with ids built
        // from their times alone. Settle those ids against the meals the day already stores, or a
        // copy can be saved over a different meal that holds the same id (#5463).
        if (!pendingDay.dayStart && pendingDay.meals.length > 0) {
          const day = DateTime.fromISO(dayKey, { zone: this.firstDayBOD.zone });
          const storedMeals = (await this.userDb.getDayMealsAsync(day)).meals ?? [];
          // and the ids filed on that day, which the meals above do not cover: a meal filed here
          // can be shown on a neighbouring day, and a copy must not take its id either (#5466)
          this.mealIdsFiledOnDay.set(dayKey, await this.userDb.getMealIdsFiledOnDayAsync(day));
          this.placePendingCopiesOnDay(pendingDay, storedMeals, dayKey);
        }
        // only save meals that are selected
        for (const meal of pendingDay.meals) {
          if (pendingDay.selectedMealIds.has(meal._id)) {
            await this.persistMealAsync(meal, transaction);
          }
        }
        // if the day was never loaded there is no document to write. copyMealsToDays
        // creates such a pending day for every target the user has not opened, so this
        // is expected rather than a failure being absorbed — and asking to save the
        // missing document would throw, because ensureCachedReference hands a falsy
        // argument straight back and assureIdForEntry then reads _id off it.
        //
        // Skipping it leaves the user nothing stale to see. A day's totals are added
        // up again from the meals actually stored, by updateDayValuesInMemory inside
        // generateBacklogInMemoryAsync, every time the backlog cache is refilled — and
        // every reader of a day goes through that cache, this page's own "which day
        // should I start on" check included. The clearBacklogCacheAsync below makes
        // the next read a fresh one. What can lag is the copy of the summary ON DISK,
        // which nothing reads directly; for a future day nobody opened there is
        // usually no stored summary at all, because the rebuild only persists a future
        // record once it is Planned. This was asked and answered in
        // https://github.com/cybsol/mh-physique/issues/5106, and the last leg of
        // client/src/e2e/repro/meal-plan-back-discards-days-5056.spec.ts holds it still.
        //
        // The meal loop above must stay OUTSIDE this guard. Widening the guard to cover
        // it would skip the whole pending day, and a day the user only ever copied meals
        // onto would lose them with nothing shown. The same E2E leg named above is what
        // catches that: it reads the stored meal documents and fails when the copy wrote
        // none (#5112).
        if (pendingDay.dayStart) {
          await this.persistDayAsync(pendingDay.dayStart, transaction);
        }
      }

      await this.userDb.clearBacklogCacheAsync();
      this.pendingDays.clear();
    } finally {
      this.loading = false;
    }
  }

  private async doneAsync() {
    let loader = this.loadingCtrl.create({ content: LL.common.loading.pleaseWait() });
    void loader.present();
    try {
      // cache current day and commit all pending changes
      this.storeDayInCache();
      await this.commitAllPendingChangesAsync();
    } catch (err) {
      // the same answer the exit dialog's "Save and exit" gives: report it, tell the user,
      // and keep them here with the days still pending so they can try again. Leaving the page
      // would discard the very days they asked to save, and without this the overlay below was
      // never dismissed and the screen stayed blocked with nothing said (#5467).
      this.logging.trackAbnormalException(err, LL.mealPlan.errors.saveFailed());
      return;
    } finally {
      void loader.dismiss();
    }
    await this.exitAsync({ completed: true });
  }

  private async nextAllAsync() {
    let loader = this.loadingCtrl.create({ content: LL.common.loading.pleaseWait() });
    void loader.present();
    try {
      while (true) {
        // cache current day (not saved to DB yet)
        this.storeDayInCache();

        this.currentDayIndex++;
        this.currentDay = this.firstDayBOD.plus({ days: this.currentDayIndex });
        if (this.currentDay > this.lastDayEOD) {
          break;
        }
        if ((await this.loadCurrentDayAsync()) === DayLoadResult.Loaded) {
          await this.fillCurrentDaySuggestionsAsync();
        }
      }

      // commit all pending changes at once and exit
      await this.commitAllPendingChangesAsync();
      this.cdRef.markForCheck();
    } catch (err) {
      // same as doneAsync (#5467). One thing more to undo here: the loop leaves the day index one
      // past the last day, which is the state this method ends in, so a failure would leave the
      // page on a day that does not exist. Step it back onto the day the screen still shows, so
      // pressing Done tries the save again.
      if (this.currentDay > this.lastDayEOD) {
        this.currentDayIndex--;
        this.currentDay = this.firstDayBOD.plus({ days: this.currentDayIndex });
      }
      this.logging.trackAbnormalException(err, LL.mealPlan.errors.saveFailed());
      this.cdRef.markForCheck();
      return;
    } finally {
      void loader.dismiss();
    }
    this.telemetry.trackEvent('action_mealplan_nextall', {});

    await this.exitAsync({ completed: true });
  }

  private toggleTabSlot(isBack: boolean) {
    this.isNavigatingBack = isBack;
    this.tabSlot = this.tabSlot === 'a' ? 'b' : 'a';
  }

  handleSwipeForward() {
    void this.nextDayAsync();
  }

  handleSwipeBack() {
    void this.prevDayAsync();
  }

  private async nextDayAsync() {
    if (!this.nextDayEnabled) return;
    // cache current day BEFORE switching slot (this.tab must still point to outgoing tab)
    this.storeDayInCache();
    this.toggleTabSlot(false);

    this.currentDayIndex++;
    this.currentDay = this.firstDayBOD.plus({ days: this.currentDayIndex });
    if ((await this.loadCurrentDayAsync()) === DayLoadResult.Loaded) {
      await this.fillCurrentDaySuggestionsAsync();
    }
    if (this.viewDestroyed) return;
    this.cdRef.markForCheck();
    this.telemetry.trackEvent('action_mealplan_next', {});
  }

  private async prevDayAsync() {
    if (!this.prevDayEnabled) return;
    // cache current day BEFORE switching slot (this.tab must still point to outgoing tab)
    this.storeDayInCache();
    this.toggleTabSlot(true);

    this.currentDayIndex--;
    this.currentDay = this.firstDayBOD.plus({ days: this.currentDayIndex });

    await this.loadCurrentDayAsync();
    if (this.viewDestroyed) return;
    this.cdRef.markForCheck();
    this.telemetry.trackEvent('action_mealplan_prev', {});
  }

  private async goToDayAsync(dayIndex: number) {
    // don't navigate if clicking the current day
    if (dayIndex === this.currentDayIndex) {
      return;
    }

    // cache current day BEFORE switching slot (this.tab must still point to outgoing tab)
    this.storeDayInCache();
    this.toggleTabSlot(dayIndex < this.currentDayIndex);

    this.currentDayIndex = dayIndex;
    this.currentDay = this.firstDayBOD.plus({ days: this.currentDayIndex });

    if ((await this.loadCurrentDayAsync()) === DayLoadResult.Loaded) {
      await this.fillCurrentDaySuggestionsAsync();
    }
    if (this.viewDestroyed) return;
    this.cdRef.markForCheck();
    this.telemetry.trackEvent('action_mealplan_goto', { dayIndex });
  }

  public async loadCurrentDayAsync(): Promise<DayLoadResult> {
    // check pending cache first to decide if loading spinner is needed
    const dayKey = this.currentDay.toISODate();
    const pendingDay = this.pendingDays.get(dayKey);

    // only show loading for uncached days (cached days load instantly, no spinner needed)
    if (!pendingDay?.dayStart) {
      this.tabLoading[this.tabSlot] = true;
    }

    // the slot this load fills. Held as the key rather than as the object, because every
    // exit below replaces that object rather than writing into it.
    const slot = this.tabSlot;
    const dayName = formatWeekdayLong(this.currentDay, BOOT_LOCALE);

    // this slot is about to be refilled, so from here until the new day is built
    // it holds nothing anybody may cache — not even the day it showed before,
    // whose pending entry may have been edited since (copyMealsToDays can add
    // meals to a day the user is not looking at). storeDayInCache reads this.
    // The slot goes on SHOWING the day it showed before; what is withdrawn is
    // only the claim about which day that is.
    this.tabData[slot] = { ...this.tabData[slot], loadedDayKey: undefined };
    this.loading = true;
    try {
      this.nextDayEnabled = this.currentDay.plus({ days: 1 }) <= this.lastDayEOD;
      this.prevDayEnabled = this.currentDay.minus({ days: 1 }) >= this.firstDayBOD;

      // the new day is built up in these locals and put into the slot in one write at each
      // exit below, so the slot never holds one day's meals beside another day's totals
      let dayStart: Log_DayStart;
      let mealLogs: Log_Meal[] = [];

      if (pendingDay && pendingDay.dayStart) {
        // load fully from cache
        dayStart = pendingDay.dayStart;
        mealLogs = pendingDay.meals;
      } else {
        // load from DB
        let result = deepClone(await this.userDb.getDayMealsAsync(this.currentDay));
        dayStart = result.day;
        mealLogs = result.meals;
        // the ids filed on this day, kept for every later copy onto it: a meal filed here can be
        // shown on a neighbouring day, so the meals above do not name all the taken ids (#5466)
        this.mealIdsFiledOnDay.set(dayKey, await this.userDb.getMealIdsFiledOnDayAsync(this.currentDay));

        // if meals were copied onto this day before it was opened, they meet its stored meals now
        if (pendingDay && pendingDay.meals.length > 0) {
          mealLogs = this.placePendingCopiesOnDay(pendingDay, mealLogs, dayKey);
          // if the day has its day document, the pending day now holds the whole day. Without one
          // the page stops just below and the day is never shown, so the pending day must keep
          // holding only the copies: commitAllPendingChangesAsync places them again against the
          // stored meals, and would otherwise treat these stored-meal clones as copies too.
          if (dayStart) {
            pendingDay.dayStart = dayStart;
            pendingDay.meals = mealLogs;
          }
        }
      }

      // if the day has no day document, it cannot show meals or be planned. The date range only
      // covers days the schedule wrote one for, so this is a gap that should not exist: report
      // it, tell the user, and empty the slot, which may still hold the day before last
      if (!dayStart) {
        this.tabData[slot] = {
          meals: [],
          displayNutrientsDay: [],
          dayName,
          dayStart: undefined,
          match_pc: undefined,
          // an empty slot belongs to no day, so nothing here may be cached
          loadedDayKey: undefined,
        };
        this.logging.trackSilentProblem('meal_plan_day_start_missing', { day: dayKey, dayIndex: this.currentDayIndex });
        this.toastCtrl.showToast({
          message: LL.mealPlan.errors.dayCouldNotBeOpened({ day: dayName }),
          showCloseButton: true,
          duration: 10_000,
        });
        return DayLoadResult.DayStartMissing;
      }
      for (let m of mealLogs) {
        if (m.basedOnFoodId) {
          await this.cacheFoodNameAsync(m.basedOnFoodId);
        }
      }

      // build meals in local array, then assign at once to avoid clearing visible content
      const newMeals: MealSuggestionEntry[] = [];
      for (let mealLog of mealLogs) {
        let meal = new MealSuggestionEntry();
        await this.fillEntryFromMealLogAsync(meal, mealLog);

        // set isSelected: from cache if available, otherwise false (user must explicitly select)
        if (pendingDay) {
          meal.isSelected = pendingDay.selectedMealIds.has(mealLog._id);
        } else {
          meal.isSelected = false;
        }

        newMeals.push(meal);
      }

      // the one write that makes this day visible. Everything above can bail out or throw
      // part-way, and until this line runs the slot still holds the previous day in full --
      // which is what must be true, because a half-built day must never be shown or recorded.
      this.tabData[slot] = {
        meals: newMeals,
        displayNutrientsDay: FormatNutrients(dayStart.nutrients, this.userDb, { targets: dayStart.targets }),
        dayName,
        dayStart,
        match_pc: calculateNutrientMatch_pc(dayStart.nutrients, dayStart.targets),
        loadedDayKey: dayKey,
      };
      return DayLoadResult.Loaded;
    } finally {
      this.loading = false;
      this.tabLoading[this.tabSlot] = false;
      this.cdRef.markForCheck();
    }
  }

  public async areAllMealsUnplanned() {
    return !this.tab.meals.some(meal => meal.mealLog.ingredients && meal.mealLog.ingredients.length > 0);
  }

  /**
   * Copy one past day's meals onto the day being planned, all of them or none of them.
   *
   * Either way of giving up is the app deciding NOT to use this day, so neither may leave the
   * meals it had already filled behind: the caller tries further days against what it finds
   * here, and the fallback after those skips any meal that already has ingredients. So an
   * abandoned attempt puts every meal back exactly as it found it (`df-9f2f7eb82990`).
   */
  public async tryToCopyEntireDayAsync(day: DateTime): Promise<boolean> {
    const tab = this.tab;
    // the entries as they were when the attempt started. loadCurrentDayAsync builds a fresh
    // array of fresh entries, so holding these is how the undo can tell afterwards whether the
    // day it filled is still the day on screen.
    const entries = tab.meals.slice();
    const mealLogs = entries.map(entry => entry.mealLog);
    const remembered = captureMealsBeforeDayCopy(mealLogs);
    // how many meals this attempt has written to, and so how many have to be put back and redrawn
    let filledCount = 0;
    let copiedWholeDay = false;
    try {
      for (let meal of entries) {
        let mealLog = meal.mealLog;
        let template = await this.findMatchByExactDayAndNameAsync(day, mealLog.name, mealLog.targets);
        if (template === undefined) {
          return false;
        }
        this.foodDb.fillMealFromOtherMeal(mealLog, template);
        mealLog.autoPlanned = true;
        filledCount++;
        await this.updateMealSuggestionAsync(meal);
      }

      let dayMatch_pc = calculateNutrientMatch_pc(tab.dayStart.nutrients, tab.dayStart.targets) || 0;
      if (dayMatch_pc < 95) {
        return false;
      }
      copiedWholeDay = true;
      return true;
    } finally {
      // also runs when the loop above threw part-way, which leaves the same half-copied day
      if (!copiedWholeDay && filledCount > 0) {
        await this.undoPartialDayCopyAsync(tab, entries, mealLogs, remembered, filledCount);
      }
    }
  }

  /**
   * Put the meals of an abandoned day copy back, and redraw what showed them.
   *
   * Restoring the stored nutrients as well as the ingredients means the numbers are right even
   * on the path where `updateMealSuggestionAsync`'s own recalculation cannot read a food and is
   * deliberately swallowed. That refresh is what rebuilds each meal's card and the day's totals,
   * match percentage included, so the page stops showing the rejected day.
   */
  private async undoPartialDayCopyAsync(
    tab: TabDayData,
    entries: MealSuggestionEntry[],
    mealLogs: Log_Meal[],
    remembered: MealDayCopyRollbackState[],
    filledCount: number,
  ) {
    // the data goes back first and unconditionally. It is the half-copied day itself, and it is
    // worth undoing whether or not the page still shows these meals.
    restoreMealsAfterFailedDayCopy(mealLogs, remembered);

    if (this.tab !== tab || !sameEntriesInOrder(tab.meals, entries)) {
      // the day was reloaded, or the page swapped to the other tab slot, while the attempt was
      // running. Redrawing now would recompute the CURRENT day's totals from the meals of a day
      // that is no longer on screen, which is worse than not redrawing at all -- so report it
      // and stop. Nothing should be able to reload a day underneath a running attempt, and a
      // day left showing a rejected copy is the bug this path exists to stop, so it must not
      // pass unrecorded.
      this.logging.trackSilentProblem('meal_plan_day_copy_meals_replaced', {
        filledCount,
        mealsWhenStarted: entries.length,
        mealsNow: this.tab === tab ? tab.meals.length : undefined,
        sameTab: this.tab === tab,
      });
      return;
    }

    for (let i = 0; i < filledCount; i++) {
      try {
        await this.updateMealSuggestionAsync(entries[i]);
      } catch (err) {
        // this runs inside a finally, sometimes with the attempt's own exception already on its
        // way up. Letting a redraw failure out would replace that exception with this one and
        // skip the remaining meals' redraws, so report it and carry on to the rest: the data is
        // already restored, and what is left here is the display catching up with it.
        this.logging.trackSilentProblem('meal_plan_day_copy_undo_refresh_failed', {
          mealIndex: i,
          error: describeErrorForLog(err),
        });
      }
    }
  }

  public async tryToCombineMealsForTodayAsync() {
    let weekday = this.currentDay.weekday;
    for (let meal of this.tab.meals) {
      let mealLog = meal.mealLog;
      // skip meals that are pre-planned
      if (mealLog.ingredients && mealLog.ingredients.length > 0) {
        continue;
      }
      // first try to tune the exact same meal from exact same weekday previously
      {
        let lastWeekMeal = await this.findMatchByDayAndNameAsync(weekday, mealLog.name, mealLog.planningTargets);
        if (lastWeekMeal !== undefined) {
          this.foodDb.fillMealFromOtherMeal(mealLog, lastWeekMeal);
          mealLog.autoPlanned = true;
          await this.updateMealSuggestionAsync(meal);
          continue;
        }
      }

      // then try to tune the exact same meal from last week
      {
        let lastDayMeal = await this.findBestMatchByNameAsync(mealLog.name, mealLog.planningTargets);
        if (lastDayMeal !== undefined) {
          this.foodDb.fillMealFromOtherMeal(mealLog, lastDayMeal);
          mealLog.autoPlanned = true;
          await this.updateMealSuggestionAsync(meal);
          continue;
        }
      }

      // then try to tune the one with most similar targets
      {
        let similarMeal = await this.findMatchByTargetsAsync(mealLog.planningTargets);
        if (similarMeal !== undefined) {
          this.foodDb.fillMealFromOtherMeal(mealLog, similarMeal);
          mealLog.autoPlanned = true;
          await this.updateMealSuggestionAsync(meal);
          continue;
        }
      }

      // if no suitable found, try suggesting new ones....
      // a shallow copy, not deepClone: deepClone drops every number that is not finite, which
      // would hide a candidate's broken numbers from the tuner before it could report them. the
      // tuner copies its own input and never changes it, and this copy only has fields replaced.
      // these are candidates to pick from, so one with broken numbers is reported, not a crash.
      let tunerParams: MealTunerParams = { ...this.tunerParams, candidates: true };
      // if planningTargets has no usable calorie target (can happen with calorie cycling mismatch,
      // or on a day whose calories are already spent), fall back to the meal's own plan figure --
      // `tunableTargetsOr` is the one place that rule lives.
      tunerParams.targets = tunableTargetsOr(mealLog.planningTargets, mealLog.targets);

      let tuner = new MealTuner();

      let results = tuner.solve(tunerParams);

      // a failed solve carries no meal list at all, so there is nothing to read off it. this path
      // deliberately carries on with none rather than giving up: the planner treats "nothing to
      // suggest" the same however it came about, and the crash itself is reported just below.
      const tunedMeals = results.outcome === MealTunerOutcome.Solved ? results.meals : [];

      for (let m of tunedMeals) {
        await this.cacheFoodNameAsync(m.id);
      }
      let match = this.findMatchFromSuggestions(tunedMeals);

      // a crash and an honest "no combination of these foods fits" both end up with no meals to
      // offer, and only the failed answer's own message separates them. filing both under
      // `meal_tuner_no_results` put them in one Sentry group under a name that says the tuner had
      // nothing to suggest, and dropped the crash's own message on the way. so ask about the
      // crash FIRST, and keep the older name for what it was always meant to describe.
      if (results.outcome === MealTunerOutcome.Failed) {
        this.logging.trackSilentProblem('meal_tuner_crashed', {
          error: results.error,
          where: 'tryToCombineMealsForTodayAsync',
          mealName: mealLog.name,
          availableMeals: tunerParams.meals?.length || 0,
          targets: tunerParams.targets,
        });
      } else if (results.meals.length === 0) {
        // track if meal tuner returns no suggestions
        this.logging.trackSilentProblem('meal_tuner_no_results', {
          mealName: mealLog.name,
          availableMeals: tunerParams.meals?.length || 0,
          targets: tunerParams.targets,
        });
      }

      // some candidates were left out because their numbers are broken (a missing weight, a
      // recipe stored with a total weight of 0). that is not a crash -- the solve answered from
      // the rest -- but without this report those candidates vanish with no trace at all. it is
      // asked of EVERY solve that ran, including one that ended up with nothing to suggest,
      // because "every candidate was broken" is exactly the case worth seeing.
      if (results.outcome === MealTunerOutcome.Solved) {
        reportInvalidTunerMeals(this.logging, results, 'tryToCombineMealsForTodayAsync');
      }

      await this.foodDb.fillMealFromTunerAsync(mealLog, match);
      mealLog.autoPlanned = true;
      await this.updateMealSuggestionAsync(meal);
    } // for meal
  }

  public async fillCurrentDaySuggestionsAsync() {
    this.loading = true;
    try {
      // in order for whole-day matching to work, there must not be any pre-planned meals
      if (await this.areAllMealsUnplanned()) {
        // first try to adjust the exact same day last week
        if (await this.tryToCopyEntireDayAsync(this.currentDay.minus({ days: 7 }))) {
          return;
        }
        // then try to adjust some other day in the past 14 days
        for (let i = 1; i < 14; i++) {
          if (await this.tryToCopyEntireDayAsync(this.currentDay.minus({ days: i }))) {
            return;
          }
        }
      }
      // if couldn't copy any of days exactly, combine individual meals
      await this.tryToCombineMealsForTodayAsync();
    } catch (err) {
      this.logging.trackAbnormalException(err, untranslatedString(`Can't setup meal plan due to some internal error, please try again later.`));
      void this.exitAfterErrorAsync();
    } finally {
      this.loading = false;
    }
  }

  async editMealAsync(meal: MealSuggestionEntry) {
    let loading = this.loadingCtrl.create();
    void loading.present();
    /* Unfortunately, we have to store the entire day, so the next page can show daily macros correctly
    To be able to fix it, we would need to make the EditMealPage be able to work with only in-memory data. */
    await this.storeDayAsync();

    void this.navCtrl.pushAsync(IEditMealPage, {
      id: meal.mealLog._id,
      returnCallback: 'onMealEdited',
      local: {
        returnContext: { mealId: meal.mealLog._id },
      },
    });
    this.cdRef.markForCheck();
    void loading.dismiss();
  }

  /**
   * Callback invoked by NavController when returning from EditMealPage.
   * This is called via the returnCallback mechanism in nav-controller.ts.
   */
  async onMealEdited(context: { mealId: string }, _result: unknown) {
    const meal = this.tab.meals.find(m => m.mealLog._id === context.mealId);
    if (meal) {
      meal.loading = true;
      this.cdRef.detectChanges();
      // a private copy, like every meal this page loads (see the day load's deepClone): the
      // tuner edits it in place and Discard only forgets pendingDays, so editing the shared
      // meal would leave a discarded plan in memory for the next save anywhere to write (#5904)
      meal.mealLog = deepClone(await this.userDb.getOneLogEntryAsync<Log_Meal>(context.mealId, this.docWatcher));
      await this.updateMealSuggestionAsync(meal);
    }
  }

  async pickMealAsync(meal: MealSuggestionEntry) {
    // Size the replacement to fill the ORIGINAL slot rather than the gap left beside the meal
    // being replaced — which means leaving that meal's own food out of the day total.
    //
    // The day's meals are already in memory here, so this calls the shared calculation directly
    // rather than the UserDb wrapper that re-reads them. This block used to spell the same
    // arithmetic out by hand; two copies of one rule is how a third caller came to get it wrong
    // (#5297).
    const day = this.tab.dayStart;
    const replaceTuningTargets = mealTargetsAsIfEmpty(
      meal.mealLog,
      this.tab.meals.map(m => m.mealLog),
      day?.targets || {},
      this.currentDay,
    );

    let params: MealSuggestionsPageParams = {
      title: meal.mealLog.name,
      mode: MealSuggestionsMode.Replace,
      returnCallback: 'onMealSuggestionResult',
      local: {
        targets: meal.mealLog.targets,
        tuningTargets: replaceTuningTargets,
        returnContext: { mealId: meal.mealLog._id },
      },
    };
    void this.navCtrl.pushAsync(IMealSuggestionsPage, params);
  }

  /**
   * Callback invoked by NavController when returning from MealSuggestionsPage.
   * This is called via the returnCallback mechanism in nav-controller.ts.
   */
  async onMealSuggestionResult(context: { mealId: string }, suggestion: MealTunerMeal | undefined) {
    const meal = this.tab.meals.find(m => m.mealLog._id === context.mealId);
    if (!meal) return;

    if (suggestion) {
      meal.loading = true;
      this.cdRef.detectChanges();
      try {
        await this.foodDb.fillMealFromTunerAsync(meal.mealLog, suggestion);
        meal.mealLog.autoPlanned = true;
        await this.updateMealSuggestionAsync(meal);
      } finally {
        // fillMealFromTunerAsync refuses rather than rebuilding a meal it could not read the
        // source of (#4535), so this can throw. The error still travels up to be reported, but
        // the card must not be left spinning on the way out.
        meal.loading = false;
        this.cdRef.markForCheck();
      }
    }
  }

  handleMealView(food: Food) {
    if (!food || isLogMealById(food._id)) return;
    void this.navCtrl.pushAsync(IEditFoodPage, { id: food._id, mode: 'view' });
  }

  handleClose() {
    // no dialog logic of its own any more: popping the page runs the leave
    // guard, which asks about unsaved days exactly as it does for back (#5056)
    void this.navCtrl.popAsync();
  }

  // formatted date range for header (e.g., "Oct 6-19th, 2025")
  get dateRangeFormatted(): string {
    if (!this.firstDayBOD || !this.lastDayEOD) {
      return '';
    }
    const startMonth = formatMonthShort(this.firstDayBOD, BOOT_LOCALE);
    const startDay = this.firstDayBOD.day;
    const endDay = this.lastDayEOD.day;
    const endYear = this.lastDayEOD.year;

    // if same month, show "Oct 6-19th, 2025"
    if (this.firstDayBOD.month === this.lastDayEOD.month) {
      return `${startMonth} ${startDay}-${endDay}, ${endYear}`;
    }
    // if different months, show "Oct 6 - Nov 2, 2025"
    const endMonth = formatMonthShort(this.lastDayEOD, BOOT_LOCALE);
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`;
  }

  // formatted day name for the navigation row
  get currentDayFormatted(): string {
    return this.currentDay ? formatWeekdayMonthDayLong(this.currentDay, BOOT_LOCALE) : '';
  }

  // convert string match to number for food-card
  getMealMatchPercent(meal: MealSuggestionEntry): number {
    return parseInt(meal.match, 10) || 0;
  }

  // get like state for food-card
  getLikeState(meal: MealSuggestionEntry): LikeState {
    const food = meal.food as Food;
    if (!food?.local?.liked) return LikeState.None;
    return food.local.liked > 0 ? LikeState.Liked : LikeState.Disliked;
  }

  // check if meal is favorited
  isFavorite(meal: MealSuggestionEntry): boolean {
    const food = meal.food as Food;
    return food?.local?.liked > 0;
  }

  // handle menu click from food-card
  async handleMealMenu(event: { event: Event; food: FoodHeader }, meal: MealSuggestionEntry) {
    const targetElement = event.event.target as HTMLElement;

    await this.popupMenu.createMenu({
      buttons: [
        {
          text: LL.mealPlan.buttons.editMeal(),
          handler: () => this.handleEditMeal(meal)
        },
        {
          text: LL.mealPlan.buttons.viewDetails(),
          handler: () => this.handleMealView(meal.food as Food)
        }
      ],
      targetFromElement: targetElement
    });
  }
}
